// Company-admin document-requirements policy (TZ SME foundation, Increment F).
// A matrix editor: which document types each employment category must have on
// file. Feeds the per-employee "Required documents" checklist and the
// documents bucket of the readiness score. Tenant admins edit; other
// managers get a read-only view.
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileCheck2, Loader2, RotateCcw, Save, ShieldAlert } from 'lucide-react';
import toast from 'react-hot-toast';
import { useRoleAccess } from '@/features/hooks/useRoleAccess';
import { getApiErrorMessage } from '@/lib/apiError';
import {
  getDocumentRequirementsPolicy,
  putDocumentRequirementsPolicy,
  EMPLOYMENT_TYPE_KEYS,
  type DocumentRequirementRules,
  type DocumentTypeOption,
} from '@/services/documentRequirementsService';

// Row model: the shared default row, one row per employment type, then the
// two additive special rows (foreign / drivers).
type RowKind = 'default' | 'employment_type' | 'foreign_extra' | 'driver_extra';
interface MatrixRow {
  key: string;
  kind: RowKind;
  label: string;
  hint?: string;
}

const deepCopyRules = (r: DocumentRequirementRules): DocumentRequirementRules => ({
  default: [...(r.default || [])],
  by_employment_type: Object.fromEntries(
    Object.entries(r.by_employment_type || {}).map(([k, v]) => [k, [...(v || [])]]),
  ),
  foreign_extra: [...(r.foreign_extra || [])],
  driver_extra: [...(r.driver_extra || [])],
  // Compliance program: carry the tenant's custom types + per-type settings
  // through the round trip (a save must never strip them).
  custom_types: (r.custom_types || []).map((c) => ({ ...c, applies_to: [...(c.applies_to || [])] })),
  type_settings: JSON.parse(JSON.stringify(r.type_settings || {})),
});

const FALLBACK_DOC_TYPES: DocumentTypeOption[] = [
  { type: 'id', label: 'National ID' },
  { type: 'passport', label: 'Passport' },
  { type: 'driver_license', label: 'Driver license' },
  { type: 'degree', label: 'Degree' },
  { type: 'certificate', label: 'Certificate' },
  { type: 'contract', label: 'Contract' },
  { type: 'other', label: 'Other' },
];

export const DocumentRequirementsPage: React.FC = () => {
  const { t } = useTranslation('employees');
  const { isManager, isTenantOwner, canAdminister } = useRoleAccess();
  const canEdit = isTenantOwner || canAdminister;

  const [docTypes, setDocTypes] = useState<DocumentTypeOption[]>(FALLBACK_DOC_TYPES);
  const [rules, setRules] = useState<DocumentRequirementRules | null>(null);
  const [defaults, setDefaults] = useState<DocumentRequirementRules | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!isManager) { setLoading(false); return; }
    let active = true;
    getDocumentRequirementsPolicy()
      .then((p) => {
        if (!active) return;
        // Prefer the tenant's rules; fall back to the platform defaults shape.
        setRules(deepCopyRules(p.rules || p.defaults));
        setDefaults(p.defaults ? deepCopyRules(p.defaults) : null);
        if (p.document_types?.length) setDocTypes(p.document_types);
      })
      .catch(() => { if (active) setLoadError(true); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [isManager]);

  const employmentTypeLabel: Record<string, string> = {
    full_time: t('doc_requirements.et_full_time', { defaultValue: 'Full-time' }),
    part_time: t('doc_requirements.et_part_time', { defaultValue: 'Part-time' }),
    casual: t('doc_requirements.et_casual', { defaultValue: 'Casual' }),
    contract: t('doc_requirements.et_contract', { defaultValue: 'Contract' }),
    fixed_term: t('doc_requirements.et_fixed_term', { defaultValue: 'Fixed-term' }),
    specific_task: t('doc_requirements.et_specific_task', { defaultValue: 'Specific task (ELRA)' }),
    intern: t('doc_requirements.et_intern', { defaultValue: 'Intern' }),
    seasonal: t('doc_requirements.et_seasonal', { defaultValue: 'Seasonal' }),
    consultant: t('doc_requirements.et_consultant', { defaultValue: 'Consultant' }),
    temporary: t('doc_requirements.et_temporary', { defaultValue: 'Temporary' }),
    other: t('doc_requirements.et_other', { defaultValue: 'Other' }),
  };

  const rows: MatrixRow[] = useMemo(() => [
    {
      key: '__default__',
      kind: 'default' as RowKind,
      label: t('doc_requirements.row_default', { defaultValue: 'All employees (default)' }),
      hint: t('doc_requirements.row_default_hint', { defaultValue: 'Applies to everyone' }),
    },
    ...EMPLOYMENT_TYPE_KEYS.map((k) => ({
      key: k,
      kind: 'employment_type' as RowKind,
      label: employmentTypeLabel[k] || k,
    })),
    {
      key: '__foreign__',
      kind: 'foreign_extra' as RowKind,
      label: t('doc_requirements.row_foreign', { defaultValue: 'Foreign employees (extra)' }),
      hint: t('doc_requirements.row_extra_hint', { defaultValue: 'Added on top of the rows above' }),
    },
    {
      key: '__driver__',
      kind: 'driver_extra' as RowKind,
      label: t('doc_requirements.row_driver', { defaultValue: 'Drivers (extra)' }),
      hint: t('doc_requirements.row_extra_hint', { defaultValue: 'Added on top of the rows above' }),
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t]);

  const listFor = (r: DocumentRequirementRules, row: MatrixRow): string[] => {
    switch (row.kind) {
      case 'default': return r.default || [];
      case 'foreign_extra': return r.foreign_extra || [];
      case 'driver_extra': return r.driver_extra || [];
      default: return r.by_employment_type?.[row.key] || [];
    }
  };

  const toggle = (row: MatrixRow, docType: string) => {
    if (!canEdit || !rules) return;
    setRules((prev) => {
      if (!prev) return prev;
      const next = deepCopyRules(prev);
      const list = listFor(next, row);
      const updated = list.includes(docType) ? list.filter((x) => x !== docType) : [...list, docType];
      switch (row.kind) {
        case 'default': next.default = updated; break;
        case 'foreign_extra': next.foreign_extra = updated; break;
        case 'driver_extra': next.driver_extra = updated; break;
        default: next.by_employment_type = { ...next.by_employment_type, [row.key]: updated };
      }
      return next;
    });
    setDirty(true);
  };

  const onResetDefaults = () => {
    if (!defaults) return;
    setRules(deepCopyRules(defaults));
    setDirty(true);
    toast(t('doc_requirements.reset_loaded', { defaultValue: 'Defaults loaded — save to apply.' }));
  };

  const [newTypeLabel, setNewTypeLabel] = useState('');
  const [newTypeScope, setNewTypeScope] = useState('');
  const addCustomType = () => {
    if (!rules) return;
    const label = newTypeLabel.trim();
    if (!label) return;
    const key = label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 64);
    if (!key || key.length < 2) { toast.error(t('doc_requirements.bad_type_name', { defaultValue: 'Give the type a longer name.' })); return; }
    const existing = new Set([...(docTypes.map((d) => d.type)), ...((rules.custom_types || []).map((c) => c.key))]);
    if (existing.has(key)) { toast.error(t('doc_requirements.type_exists', { defaultValue: 'A type with that name already exists.' })); return; }
    const next = deepCopyRules(rules);
    next.custom_types = [...(next.custom_types || []), { key, label, ...(newTypeScope ? { applies_to: [newTypeScope] } : {}) }];
    setRules(next); setDirty(true); setNewTypeLabel(''); setNewTypeScope('');
  };
  const removeCustomType = (key: string) => {
    if (!rules) return;
    const next = deepCopyRules(rules);
    next.custom_types = (next.custom_types || []).filter((c) => c.key !== key);
    // also drop it from any matrix lists so validation stays clean
    next.default = next.default.filter((d) => d !== key);
    for (const k of Object.keys(next.by_employment_type)) {
      next.by_employment_type[k] = next.by_employment_type[k].filter((d) => d !== key);
    }
    setRules(next); setDirty(true);
  };

  const onSave = async () => {
    if (!rules) return;
    setSaving(true);
    try {
      await putDocumentRequirementsPolicy(rules);
      // Round-trip verification: re-read from the backend so what the screen
      // shows is what actually PERSISTED, not the request echo.
      const fresh = await getDocumentRequirementsPolicy();
      if (fresh?.rules) setRules(deepCopyRules(fresh.rules));
      setDirty(false);
      toast.success(t('doc_requirements.saved', { defaultValue: 'Document requirements saved.' }));
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, t('doc_requirements.save_failed', {
        defaultValue: "We couldn't save the document requirements. Your previous configuration has not been changed." })));
    } finally {
      setSaving(false);
    }
  };

  if (!isManager) {
    return (
      <div className="max-w-md mx-auto mt-16 text-center">
        <ShieldAlert className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
        <h1 className="text-h3 text-charcoal">{t('doc_requirements.admins_only', { defaultValue: 'Managers & admins only' })}</h1>
        <p className="text-muted-foreground mt-1">
          {t('doc_requirements.no_permission', { defaultValue: 'You do not have permission to view document requirements.' })}
        </p>
      </div>
    );
  }

  const primaryBtn = 'h-10 px-4 rounded-lg bg-gradient-to-br from-orange-500 to-amber-500 text-white font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50';
  const ghostBtn = 'h-10 px-4 rounded-lg border border-border text-foreground hover:bg-muted inline-flex items-center gap-2 disabled:opacity-50';

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div>
        <h1 className="text-h2 text-charcoal flex items-center gap-2">
          <FileCheck2 className="w-6 h-6" /> {t('doc_requirements.title', { defaultValue: 'Document Requirements' })}
        </h1>
        <p className="text-muted-foreground mt-1">
          {t('doc_requirements.subtitle', {
            defaultValue: 'Which documents each employment category must have on file. These feed the employee readiness score.',
          })}
        </p>
      </div>

      {!canEdit && !loading && !loadError && (
        <div className="rounded-lg px-3 py-2 text-sm border bg-warning-soft border-warning text-warning-strong">
          {t('doc_requirements.read_only', { defaultValue: 'Read-only view — only company admins can change this policy.' })}
        </div>
      )}

      {loading ? (
        <div className="py-12 flex items-center justify-center text-muted-foreground">
          <Loader2 className="w-5 h-5 animate-spin mr-2" />
          {t('doc_requirements.loading_policy', { defaultValue: 'Loading document requirements…' })}
        </div>
      ) : loadError || !rules ? (
        <div className="rounded-lg px-3 py-2 text-sm border bg-danger-soft border-danger text-danger-strong">
          {t('doc_requirements.load_error', { defaultValue: 'Could not load document requirements.' })}
        </div>
      ) : (
        <>
          <div className="rounded-2xl border border-border bg-card overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left font-medium text-muted-foreground px-4 py-3 min-w-[220px]">
                    {t('doc_requirements.col_category', { defaultValue: 'Employment category' })}
                  </th>
                  {docTypes.map((d) => (
                    <th key={d.type} className="text-center font-medium text-muted-foreground px-3 py-3 whitespace-nowrap">
                      {d.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {rows.map((row) => {
                  const special = row.kind !== 'employment_type';
                  const list = listFor(rules, row);
                  return (
                    <tr key={row.key} className={special ? 'bg-muted/40' : ''}>
                      <td className="px-4 py-2.5">
                        <span className={`text-foreground ${special ? 'font-medium' : ''}`}>{row.label}</span>
                        {row.hint && <p className="text-xs text-muted-foreground">{row.hint}</p>}
                      </td>
                      {docTypes.map((d) => (
                        <td key={d.type} className="text-center px-3 py-2.5">
                          <input
                            type="checkbox"
                            className="w-4 h-4 accent-orange-500 disabled:cursor-not-allowed"
                            checked={list.includes(d.type)}
                            disabled={!canEdit || saving}
                            onChange={() => toggle(row, d.type)}
                            aria-label={t('doc_requirements.cell_aria', {
                              defaultValue: '{{doc}} required for {{category}}',
                              doc: d.label,
                              category: row.label,
                            })}
                          />
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Tenant custom document types (compliance program §4): a customer-
              specific requirement never needs a KaziPeople deployment. */}
          <div className="rounded-xl border border-border p-4 space-y-3">
            <div>
              <h2 className="text-sm font-semibold text-foreground">{t('doc_requirements.custom_title', { defaultValue: 'Custom document types' })}</h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                {t('doc_requirements.custom_sub', { defaultValue: 'Company-specific documents (e.g. Forklift Certificate, Company NDA). Required by default; scope to one category or leave for everyone.' })}
              </p>
            </div>
            {(rules?.custom_types || []).length > 0 && (
              <ul className="space-y-2">
                {(rules?.custom_types || []).map((c) => (
                  <li key={c.key} className="flex items-center gap-3 text-sm">
                    <span className="font-medium text-foreground">{c.label}</span>
                    <span className="text-xs text-muted-foreground">
                      {(c.applies_to && c.applies_to.length)
                        ? c.applies_to.map((a) => employmentTypeLabel[a] || a).join(', ')
                        : t('doc_requirements.everyone', { defaultValue: 'All employees' })}
                    </span>
                    {canEdit && (
                      <button type="button" className="text-xs text-red-600 hover:underline ml-auto" onClick={() => removeCustomType(c.key)}>
                        {t('doc_requirements.remove', { defaultValue: 'Remove' })}
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
            {canEdit && (
              <div className="flex flex-wrap items-center gap-2">
                <input value={newTypeLabel} onChange={(e) => setNewTypeLabel(e.target.value)}
                  placeholder={t('doc_requirements.new_type_placeholder', { defaultValue: 'e.g. Forklift Operator Certificate' })}
                  className="px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm min-w-[260px]" />
                <select value={newTypeScope} onChange={(e) => setNewTypeScope(e.target.value)}
                  className="px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm">
                  <option value="">{t('doc_requirements.everyone', { defaultValue: 'All employees' })}</option>
                  {EMPLOYMENT_TYPE_KEYS.map((k) => <option key={k} value={k}>{employmentTypeLabel[k] || k}</option>)}
                </select>
                <button type="button" className={ghostBtn} onClick={addCustomType} disabled={!newTypeLabel.trim()}>
                  {t('doc_requirements.add_type', { defaultValue: 'Add type' })}
                </button>
              </div>
            )}
          </div>

          {canEdit && (
            <div className="flex flex-wrap items-center gap-2">
              <button className={primaryBtn} onClick={onSave} disabled={saving || !dirty}>
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {saving
                  ? t('doc_requirements.saving', { defaultValue: 'Saving…' })
                  : t('doc_requirements.save', { defaultValue: 'Save changes' })}
              </button>
              <button className={ghostBtn} onClick={onResetDefaults} disabled={saving || !defaults}>
                <RotateCcw className="w-4 h-4" />
                {t('doc_requirements.reset', { defaultValue: 'Reset to defaults' })}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default DocumentRequirementsPage;
